import { useState, useRef, useEffect, useMemo } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import './ui-components.css';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function formatDate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function parseDate(str) {
    if (!str) return null;
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
}

function formatDisplay(str) {
    if (!str) return '';
    const d = parseDate(str);
    if (!d) return str;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function CustomDatePicker({ value, onChange, placeholder = 'Select date', disabled }) {
    const [open, setOpen] = useState(false);
    const selected = parseDate(value);
    const [viewMonth, setViewMonth] = useState(selected?.getMonth() ?? new Date().getMonth());
    const [viewYear, setViewYear] = useState(selected?.getFullYear() ?? new Date().getFullYear());
    const ref = useRef(null);

    useEffect(() => {
        const handleClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    // When value changes externally, sync the view
    useEffect(() => {
        if (value) {
            const d = parseDate(value);
            if (d) {
                setViewMonth(d.getMonth());
                setViewYear(d.getFullYear());
            }
        }
    }, [value]);

    const days = useMemo(() => {
        const firstDay = new Date(viewYear, viewMonth, 1).getDay();
        const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
        const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

        const cells = [];
        // Previous month trailing days
        for (let i = firstDay - 1; i >= 0; i--) {
            const d = daysInPrevMonth - i;
            const date = new Date(viewYear, viewMonth - 1, d);
            cells.push({ date, day: d, outside: true });
        }
        // Current month
        for (let d = 1; d <= daysInMonth; d++) {
            const date = new Date(viewYear, viewMonth, d);
            cells.push({ date, day: d, outside: false });
        }
        // Next month leading days
        const remaining = 42 - cells.length;
        for (let d = 1; d <= remaining; d++) {
            const date = new Date(viewYear, viewMonth + 1, d);
            cells.push({ date, day: d, outside: true });
        }
        return cells;
    }, [viewMonth, viewYear]);

    const today = formatDate(new Date());

    const prevMonth = () => {
        if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
        else setViewMonth(m => m - 1);
    };

    const nextMonth = () => {
        if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
        else setViewMonth(m => m + 1);
    };

    const selectDay = (date) => {
        onChange(formatDate(date));
        setOpen(false);
    };

    const setShortcut = (daysFromNow) => {
        const d = new Date();
        d.setDate(d.getDate() + daysFromNow);
        onChange(formatDate(d));
        setOpen(false);
    };

    return (
        <div className="date-picker" ref={ref}>
            <button
                type="button"
                className={`btn btn-tertiary date-picker__trigger ${!value ? 'date-picker__trigger--placeholder' : ''} ${disabled ? 'date-picker__trigger--disabled' : ''}`}
                onClick={() => !disabled && setOpen(!open)}
                disabled={disabled}
            >
                <Calendar size={14} />
                <span>{value ? formatDisplay(value) : placeholder}</span>
                {value && !disabled && (
                    <span
                        className="date-picker__clear"
                        onClick={(e) => { e.stopPropagation(); onChange(''); }}
                        role="button"
                    >
                        <X size={12} />
                    </span>
                )}
            </button>

            {open && (
                <div className="date-picker__popup">
                    <div className="date-picker__nav">
                        <button type="button" className="btn btn-ghost btn-sm btn-icon date-picker__nav-btn" onClick={prevMonth}>
                            <ChevronLeft size={16} />
                        </button>
                        <span className="date-picker__month-label">
                            {MONTHS[viewMonth]} {viewYear}
                        </span>
                        <button type="button" className="btn btn-ghost btn-sm btn-icon date-picker__nav-btn" onClick={nextMonth}>
                            <ChevronRight size={16} />
                        </button>
                    </div>
                    <div className="date-picker__weekdays">
                        {WEEKDAYS.map(d => (
                            <span key={d} className="date-picker__weekday">{d}</span>
                        ))}
                    </div>
                    <div className="date-picker__days">
                        {days.map((cell, i) => {
                            const dateStr = formatDate(cell.date);
                            const isToday = dateStr === today;
                            const isSelected = dateStr === value;
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    className={[
                                        'date-picker__day',
                                        cell.outside && 'btn btn-ghost date-picker__day--outside',
                                        isToday && 'date-picker__day--today',
                                        isSelected && 'date-picker__day--selected',
                                    ].filter(Boolean).join(' ')}
                                    onClick={() => selectDay(cell.date)}
                                >
                                    {cell.day}
                                </button>
                            );
                        })}
                    </div>
                    <div className="date-picker__shortcuts hide-scrollbar">
                        <button type="button" className="btn btn-tertiary btn-sm date-picker__shortcut" onClick={() => setShortcut(0)}>Today</button>
                        <button type="button" className="btn btn-tertiary btn-sm date-picker__shortcut" onClick={() => setShortcut(1)}>Tomorrow</button>
                        <button type="button" className="btn btn-tertiary btn-sm date-picker__shortcut" onClick={() => setShortcut(2)}>In 2 Days</button>
                        <button type="button" className="btn btn-tertiary btn-sm date-picker__shortcut" onClick={() => setShortcut(3)}>In 3 Days</button>
                        <button type="button" className="btn btn-tertiary btn-sm date-picker__shortcut" onClick={() => {
                            const d = new Date();
                            const day = d.getDay();
                            const add = day === 6 ? 0 : day === 0 ? 6 : 6 - day;
                            setShortcut(add);
                        }}>This Weekend</button>
                        <button type="button" className="btn btn-tertiary btn-sm date-picker__shortcut" onClick={() => setShortcut(7)}>Next Week</button>
                        <button type="button" className="btn btn-tertiary btn-sm date-picker__shortcut" onClick={() => setShortcut(30)}>+1 Month</button>
                    </div>
                </div>
            )}
        </div>
    );
}
